// State of a server-paged report table (Details, Monthly, Daily), behind the PAGED_REPORTS switch (Phase 4C-4; lud-backend/docs/architecture.md, Reporting and analysis).
//
//   filters      the URL is the source of truth (from, to, app, store, branch, status, cause, sort, open store); browser back / forward and reload restore
//                them. No from / to = the rolling 12 months, so a plain link keeps following the calendar. Only the active view requests anything:
//                Reports.jsx keeps every view mounted.
//   summary      one row per store with the totals of the WHOLE filtered set (never a page)
//   hierarchy    every level comes from server totals and loads when it opens, so no month or branch can be hidden behind a page of rows:
//                  store open  -> `groups`: one total per month of the period
//                  month open  -> `detail`: its days / apps / branches (the table's `detailGroupBy`)
//                  leaf open   -> `leaf`: the rows of that one node (Faults: orders of a branch, 25 per page; Daily: a day; Monthly: a month)
//   exportAll    every authorized filtered record, not what is on screen
//   requestDelete / confirmDelete   previews the rows of the applied filters, shows count and period, then deletes exactly those rows inside that scope
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Context } from "../../context/Context";
import { useRestaurantAccess } from "../../access/hooks/useRestaurantAccess";
import useReportQuery, { invalidateReportCache, reportCacheKey } from "../../utils/useReportQuery";
import { deleteReportRows, downloadReportExport, listAllReportRows, listReport, reportIds, reportOptions, reportParams, reportSummary } from "../../utils/api/reportQuery";
import { filtersFromSearchParams, filtersToSearchParams, hasActiveReportFilters } from "../../utils/reportFilters";
import { resolveRange } from "../../utils/reportMonths";
import { translations } from "../../utils/lang";
import { getReportText } from "./reportText";
import { anchorRows, mergeMonthDetail } from "./reportUtils";

export const PAGE_SIZE = 25;
const MONTH_LEVEL = ["month"];
const NO_DELETE = { checking: false, ids: null, error: "", busy: false, preview: null };

// The four sort choices of the screens: the chronological key, or the report's main amount column.
export const sortChoiceOf = (filters, amountField) => {
  if (filters.sort === amountField) return filters.dir === "asc" ? "amountAsc" : "amountDesc";
  return filters.dir === "asc" ? "dateAsc" : "dateDesc";
};
const sortFromChoice = (choice, amountField) => ({
  dateDesc: { sort: "", dir: "" },
  dateAsc: { sort: "date", dir: "asc" },
  amountDesc: { sort: amountField, dir: "desc" },
  amountAsc: { sort: amountField, dir: "asc" },
}[choice] || { sort: "", dir: "" });

// Which rows open a leaf: Faults a branch (paged), Daily a day and Monthly a month (few rows, loaded whole).
const leafOf = (kind, node) => {
  if (kind === "compensation") return node.branchRefs ? { from: node.month, to: node.month, apps: [node.app], branchRefs: node.branchRefs } : null;
  if (kind === "daily") return node.date ? { from: node.date, to: node.date } : null;
  return node.month ? { from: node.month, to: node.month } : null;
};

export default function usePagedReport({ kind, detailGroupBy, amountField, active = true }) {
  const { lang, handleTokenExpiration } = useContext(Context);
  const { can, canForAll } = useRestaurantAccess();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
  const text = getReportText(lang);
  const token = localStorage.getItem("auth-token");
  const enabled = Boolean(active && token);
  const range = resolveRange(filters);
  const scope = {
    from: range.from,
    to: range.to,
    apps: filters.apps,
    stores: filters.stores,
    branches: filters.branches,
    orderStatuses: filters.orderStatuses,
    causes: filters.causes,
  };
  const scopeKey = JSON.stringify(scope);

  // No token at all is an expired session (a failed request is not: 401 is handled by the central response guard).
  // The context recreates handleTokenExpiration on every provider render, so a ref keeps this effect to one run per change.
  const expireRef = useRef(handleTokenExpiration);
  expireRef.current = handleTokenExpiration;
  useEffect(() => {
    if (active && !token) expireRef.current();
  }, [active, token]);

  const [notice, setNotice] = useState("");
  const [exporting, setExporting] = useState(false);
  // A preview belongs to the filters it was made under: any change of them drops it (and a late answer for old filters is never shown).
  const [deleteHeld, setDeleteHeld] = useState({ scopeKey, ...NO_DELETE });
  const deleteState = deleteHeld.scopeKey === scopeKey ? deleteHeld : NO_DELETE;
  const setDelete = (patch, key = scopeKey) => setDeleteHeld((current) => ({ ...(current.scopeKey === key ? current : NO_DELETE), ...patch, scopeKey: key }));

  const update = useCallback(
    (patch) => {
      setNotice("");
      setSearchParams(filtersToSearchParams({ ...filters, page: 1, ...patch }, searchParams));
    },
    [filters, searchParams, setSearchParams],
  );

  const options = useReportQuery(
    reportCacheKey(token, `${kind}-options`, { from: range.from, to: range.to }),
    (signal) => reportOptions(kind, { from: range.from, to: range.to }, { token, signal }),
    { enabled },
  );
  const summary = useReportQuery(
    reportCacheKey(token, `${kind}-summary`, scope),
    (signal) => reportSummary(kind, scope, { token, signal }),
    { enabled },
  );

  const stores = useMemo(() => (summary.data && summary.data.stores) || [], [summary.data]);
  // Only a store that is still part of the filtered result can stay open.
  const openStore = enabled && filters.open && stores.some((store) => String(store.storeId) === filters.open) ? filters.open : null;
  const openStoreRow = stores.find((store) => String(store.storeId) === openStore) || null;

  // The open path below the store. It belongs to these filters and this store: changing either starts collapsed again.
  const hierarchyKey = `${scopeKey}|${openStore}`;
  const [held, setHeld] = useState({ key: hierarchyKey, node: {}, page: 1 });
  const node = held.key === hierarchyKey ? held.node : {};
  const leafPage = held.key === hierarchyKey ? held.page : 1;
  const onNode = useCallback((next) => setHeld({ key: hierarchyKey, node: next, page: 1 }), [hierarchyKey]);

  const groupFilters = { ...scope, stores: [openStore], groupBy: MONTH_LEVEL };
  const groups = useReportQuery(
    reportCacheKey(token, `${kind}-groups`, groupFilters),
    (signal) => reportSummary(kind, groupFilters, { token, signal }),
    { enabled: enabled && Boolean(openStore) },
  );
  const detailFilters = { ...scope, stores: [openStore], from: node.month, to: node.month, groupBy: detailGroupBy };
  const detail = useReportQuery(
    reportCacheKey(token, `${kind}-detail`, detailFilters),
    (signal) => reportSummary(kind, detailFilters, { token, signal }),
    { enabled: enabled && Boolean(openStore && node.month) },
  );

  const leafScope = leafOf(kind, node);
  const paged = kind === "compensation";
  const leafFilters = { ...scope, stores: [openStore], ...leafScope, sort: filters.sort, dir: filters.dir, ...(paged ? { page: leafPage, limit: PAGE_SIZE } : {}) };
  const leaf = useReportQuery(
    reportCacheKey(token, `${kind}-leaf`, leafFilters),
    (signal) => (paged ? listReport : listAllReportRows)(kind, leafFilters, { token, signal }),
    { enabled: enabled && Boolean(openStore && leafScope) },
  );

  // A deleted last row can leave the current page past the end: step back to the last page.
  const pagination = leaf.data && leaf.data.pagination;
  useEffect(() => {
    if (pagination && leafPage > pagination.totalPages) setHeld((current) => ({ ...current, page: Math.max(1, pagination.totalPages) }));
  }, [pagination, leafPage]);

  const monthGroups = groups.data && Array.isArray(groups.data.groups) ? groups.data.groups : null;
  const detailGroups = detail.data && Array.isArray(detail.data.groups) ? detail.data.groups : null;
  const groupRows = useMemo(() => (monthGroups ? mergeMonthDetail(monthGroups, node.month, detailGroups) : null), [monthGroups, node.month, detailGroups]);
  const anchors = useMemo(() => (groupRows ? anchorRows(kind, groupRows, openStoreRow ? openStoreRow.store : "") : null), [kind, groupRows, openStoreRow]);

  // Checkbox choices from the server's option lists (apps, stores and branches inside the caller's scope and the chosen period).
  const optionData = options.data || {};
  const appMap = translations[lang].filter.apps;
  const statusMap = translations[lang].filter.orderStatus;
  const appLabel = (val) => appMap?.[val] ?? `${translations[lang].notApp} ${val[val.length - 1]}`;
  const selectedStores = filters.stores;
  const branchNames = useMemo(() => {
    const seen = new Map();
    (optionData.branches || [])
      .filter((branch) => !selectedStores.length || selectedStores.includes(String(branch.storeId)))
      .forEach((branch) => {
        if (branch.name && !seen.has(branch.name.toLowerCase())) seen.set(branch.name.toLowerCase(), branch.name);
      });
    return [...seen.values()];
  }, [optionData.branches, selectedStores]);
  const choices = {
    app: (optionData.apps || []).map((val) => ({ value: val, label: appLabel(val) })),
    store: (optionData.stores || []).map((store) => ({ value: String(store.storeId), label: store.name })),
    branch: branchNames.map((name) => ({ value: name, label: name })),
    status: (optionData.orderStatuses || []).map((val) => ({ value: val, label: statusMap?.[val.toLowerCase()] ?? val })),
    cause: (optionData.causes || []).map((val) => ({ value: val, label: val })),
  };
  // Changing the stores drops the branch choices that no longer belong to any selected store (as the unpaged tables do).
  const changeStores = (selected) => {
    const available = new Set((optionData.branches || []).filter((branch) => !selected.length || selected.includes(String(branch.storeId))).map((branch) => branch.name));
    update({ stores: selected, branches: filters.branches.filter((name) => available.has(name)) });
  };

  const refresh = useCallback(() => {
    invalidateReportCache(`|${kind}-`);
    options.reload();
    summary.reload();
    groups.reload();
    detail.reload();
    leaf.reload();
  }, [kind, options, summary, groups, detail, leaf]);

  const toggleStore = (storeId) => update({ open: filters.open === String(storeId) ? "" : String(storeId) });
  const clearFilters = () => update({ from: "", to: "", apps: [], stores: [], branches: [], orderStatuses: [], causes: [], open: "" });

  const exportAll = async () => {
    if (exporting) return;
    setExporting(true);
    setNotice("");
    const result = await downloadReportExport(kind, { ...scope, sort: filters.sort, dir: filters.dir }, { token });
    setExporting(false);
    if (!result.ok && !result.aborted) setNotice(result.status === 413 ? text.tooManyRows : text.exportFailed);
  };

  // Step 1 of "delete all filtered": the server lists the rows of the applied filters (needs report.bulkDelete) and states their count, period and stores.
  const requestDelete = async () => {
    if (!can("report.bulkDelete") || deleteState.checking) return;
    const previewKey = scopeKey;
    setNotice("");
    setDelete({ ...NO_DELETE, checking: true });
    const result = await reportIds(kind, scope, { token });
    if (!result.ok || !result.data || !Array.isArray(result.data.ids)) {
      setDelete(NO_DELETE, previewKey);
      setNotice(result.status === 413 ? text.tooManyRows : text.deleteListFailed);
      return;
    }
    if (!result.data.ids.length) {
      setDelete(NO_DELETE, previewKey);
      setNotice(text.empty);
      return;
    }
    setDelete({ ...NO_DELETE, ids: result.data.ids, preview: { period: result.data.period, stores: result.data.stores || [], scope } }, previewKey);
  };

  const cancelDelete = () => setDelete(NO_DELETE);

  // Step 2: after the confirmation dialog. The permission is re-checked against every restaurant that owns a listed row; the server rebuilds the scope
  // and refuses (409) when the rows changed after the preview, in which case nothing was deleted.
  const confirmDelete = async () => {
    const list = deleteState.ids;
    if (!list || !list.length || deleteState.busy) return;
    const storeIds = [...new Set(list.map((item) => item.storeId))];
    if (!can("report.bulkDelete") || !canForAll("report.entry.delete", storeIds)) {
      setDelete({ error: text.actionFailed });
      return;
    }
    setDelete({ busy: true, error: "" });
    const result = await deleteReportRows(kind, list.map((item) => item._id), reportParams(deleteState.preview.scope), { token });
    if (result.ok) {
      setDelete(NO_DELETE);
      refresh();
    } else if (result.status === 409) {
      setDelete(NO_DELETE);
      setNotice(text.deleteScopeChanged);
      refresh();
    } else {
      setDelete({ busy: false, error: text.actionFailed });
    }
  };

  const leafRows = leafScope && !leaf.loading && leaf.data && Array.isArray(leaf.data.data) ? leaf.data.data : [];
  return {
    text,
    filters,
    range,
    applyRange: ({ from, to }) => update({ from, to }),
    resetRange: () => update({ from: "", to: "" }),
    update,
    clearFilters,
    hasFilters: hasActiveReportFilters(filters),
    sortChoice: sortChoiceOf(filters, amountField),
    setSortChoice: (choice) => update(sortFromChoice(choice, amountField)),
    options,
    choices,
    changeStores,
    summary,
    stores,
    totals: (summary.data && summary.data.totals) || null,
    openStore,
    toggleStore,
    hierarchyKey,
    groups,
    groupRows,
    anchors,
    hierarchy: {
      onNode,
      ascending: filters.sort === "date" && filters.dir === "asc",
      detailPending: Boolean(node.month) && !detailGroups?.some((group) => group.month === node.month),
      detail: { loading: detail.loading, error: detail.error, onRetry: detail.reload },
      leafRows,
      leaf: { loading: Boolean(leafScope) && leaf.loading, error: leaf.error, onRetry: leaf.reload },
      leafPagination: paged && leafScope && !leaf.loading ? pagination : null,
    },
    setPage: (page) => setHeld((current) => ({ ...current, page })),
    refresh,
    exportAll,
    exporting,
    requestDelete,
    cancelDelete,
    confirmDelete,
    deleteState,
    notice,
    canBulkDelete: can("report.bulkDelete"),
  };
}
